// frontend/src/pages/contact/ContactAdministrator.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api'; // Assuming you have an API utility
import '../auth/auth.css';
import logo from '../../assets/logo.png';

const ContactAdministrator = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '', // Now mandatory
    organizationName: '', // Will be selected from dropdown or 'Not Applicable'
    designation: '',
    userType: 'customer', // Default to customer
    message: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]); // State for organizations dropdown
  const [orgsLoading, setOrgsLoading] = useState(true); // Loading state for organizations

  // Fetch organizations on component mount or when userType changes
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setOrgsLoading(true);
        let apiUrl = '/api/public/organizations';

        // Conditionally add type filter based on formData.userType
        // Only add 'type' query param if userType is customer, partner, or internal_staff
        // The backend will handle the filtering based on this type.
        if (formData.userType === 'customer' || formData.userType === 'partner' || formData.userType === 'internal_staff') {
            apiUrl += `?type=${formData.userType}`;
        }

        const response = await api.get(apiUrl);

        if (response.data && Array.isArray(response.data.data)) {
          setOrganizations(response.data.data);
          // When organizations are loaded, if userType is internal_staff, ensure organizationName is "Not Applicable"
          // Otherwise, if current organizationName is 'Not Applicable' but userType is not internal_staff, clear it.
          if (formData.userType === 'internal_staff') {
              setFormData(prev => ({ ...prev, organizationName: 'Not Applicable' }));
          } else if (formData.organizationName === 'Not Applicable') {
              setFormData(prev => ({ ...prev, organizationName: '' }));
          }
        } else {
          console.warn('No organizations data received or format is unexpected:', response.data);
          setOrganizations([]);
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
        setError('Failed to load organization list. Please try again later.'); // User-facing error
        setOrganizations([]); // Ensure it's an empty array on error
      } finally {
        setOrgsLoading(false);
      }
    };

    fetchOrganizations();
  }, [formData.userType]); // Dependency array includes formData.userType

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Reset organizationName when userType changes to force re-selection
    if (name === 'userType') {
        if (value === 'internal_staff') {
            setFormData(prev => ({ ...prev, organizationName: 'Not Applicable' }));
        } else {
            setFormData(prev => ({ ...prev, organizationName: '' })); // Clear selection for customer/partner
        }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setMessage('');

    // Determine if Organization Name is required based on userType
    const isOrganizationRequired = formData.userType === 'customer' || formData.userType === 'partner';

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber || !formData.designation || !formData.userType || (isOrganizationRequired && !formData.organizationName)) {
      setError('Please fill in all required fields (First Name, Last Name, Email, Phone Number, Designation, Account Type, and Organization Name if applicable).');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!/^\+?[0-9\s-()]{7,20}$/.test(formData.phoneNumber.trim())) {
      setError('Please enter a valid phone number (7-20 digits, optional + prefix, spaces, hyphens, parentheses allowed).');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/account-requests', formData);
      setMessage(response.data.message || 'Account request submitted successfully!');
      // Clear form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        organizationName: '', // Reset to empty for dropdown
        designation: '',
        userType: 'customer',
        message: ''
      });
    } catch (err) {
      console.error('Account request error:', err.response?.data?.message || err.message);
      setError(err.response?.data?.message || 'Failed to submit account request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if Organization Name field should be a dropdown or disabled input
  const isInternalStaff = formData.userType === 'internal_staff';

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Company Logo" style={{ height: '72px' }} />
          <h2>Request New Account</h2>
          <p>Fill in your details to request an account</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="firstName">First Name <span className="required">*</span></label>
            <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" required />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name <span className="required">*</span></label>
            <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" required />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address <span className="required">*</span></label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="john.doe@example.com" required />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number <span className="required">*</span></label>
            <input type="tel" id="phoneNumber" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="+1234567890" required />
          </div>

          <div className="form-group">
            <label htmlFor="designation">Designation <span className="required">*</span></label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              placeholder="e.g., Software Engineer, Project Manager"
              required
            />
          </div>

          {/* Moved Account Type field above Organization Name */}
          <div className="form-group">
            <label htmlFor="userType">Account Type <span className="required">*</span></label>
            <select id="userType" name="userType" value={formData.userType} onChange={handleChange} required>
              <option value="customer">Customer</option>
              <option value="partner">Partner</option>
              <option value="internal_staff">Internal Staff</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="organizationName">Organization Name <span className="required">*</span></label>
            {orgsLoading ? (
              <input type="text" value="Loading organizations..." disabled />
            ) : (
              <select
                id="organizationName"
                name="organizationName"
                value={isInternalStaff ? 'Not Applicable' : formData.organizationName}
                onChange={handleChange}
                required={!isInternalStaff} // Required only if not internal staff
                disabled={isInternalStaff} // Disable for internal staff
              >
                <option value="">-- Select Organization --</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.name}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message">Message (optional)</label>
            <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="4" placeholder="Any additional details or reason for requesting an account..."></textarea>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading || orgsLoading} // Disable if organizations are still loading
          >
            {isLoading ? (
              <>
                <span className="spinner"></span> Sending Request...
              </>
            ) : 'Submit Account Request'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ContactAdministrator;